/**
 * Traduction d'un appel à l'API RC Platform en mesure Matomo.
 *
 * Toutes les écritures métier de l'application passent par `api/<module>/<modèle>/…` :
 * les classer ici couvre chaque module sans instrumenter ses composants un par un.
 * Fonctions pures, sans Angular, pour rester testables isolément.
 */
import { applicationDeLAppel, libelleRessource, verbeMetier, VERBES_CRUD } from './matomo-plan';

/** Ressources dont les écritures sont techniques : jeton, préférences, présence… */
const RESSOURCES_BRUIT = new Set([
    'token/*',
    'users/config',
    'users/me',
    'users/status',
    'users/presence',
    'users/connection-days',
    'users/table-views',
    'extras/bandeaux-user-infos',
    'messagerie/link-preview',
    'chat-ai/backoffice/contact-filter-preview',
]);

/**
 * Actions automatiques, sans geste métier de l'utilisateur : chronomètres,
 * accusés de lecture, aperçus, synchronisations de fond, mini-jeux saisonniers.
 */
const ACTIONS_BRUIT = /^(me|preferences|heartbeat|ping|keepalive|read|preview|preview-contacts|analyser-phrase|apercu-pdf|update-temps|update-pdf-state|mark[-_].*|sync[-_]read[-_]receipts|bulk-sync|[a-z]+-game)$/;

/** Segment variable d'URL (clé numérique, UUID, jeton) : sans valeur pour l'analyse. */
function estIdentifiant(segment: string): boolean {
    return /^\d+$/.test(segment)
        || /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)
        || segment.length >= 24;
}

export interface ApiRoute {
    /** Module applicatif (`crm`, `storage-proxy`…). */
    module: string;
    /** Modèle ou ressource (`contacts`, `transfert-fichiers`…). */
    ressource: string;
    /** Action personnalisée DRF (`request-upload`, `archive`…), si présente. */
    action: string | null;
    /** La requête vise un objet précis (`…/42/`). */
    detail: boolean;
    /** Chemin sans identifiants : `api/crm/contacts/:id/archive/`. */
    chemin: string;
}

/** Découpe l'URL d'un appel API ; `null` hors API RC Platform. */
export function parseApiUrl(url: string): ApiRoute | null {
    const sansRequete = url.split(/[?#]/)[0];
    const index = sansRequete.indexOf('/api/');
    const relatif = index >= 0 ? sansRequete.slice(index + 5) : sansRequete.startsWith('api/') ? sansRequete.slice(4) : null;
    if (relatif === null) {
        return null;
    }
    const segments = relatif.split('/').filter(Boolean);
    if (segments.length < 2) {
        return null;
    }
    let [module, ressource, ...reste] = segments;
    // Ressources DRF enregistrées avec un préfixe (`backoffice/tools`).
    if (ressource === 'backoffice' && reste.length && !estIdentifiant(reste[0])) {
        ressource = `${ressource}/${reste.shift()}`;
    }
    // Action DRF = premier segment non identifiant ; tout ce qui suit (paramètre,
    // jeton public comme `sign/<jeton>`) est masqué, il ne doit jamais remonter.
    const indexAction = reste.findIndex(s => !estIdentifiant(s));
    const action = indexAction >= 0 ? reste[indexAction] : null;
    const detail = (indexAction >= 0 ? indexAction : reste.length) > 0;
    const suite = reste.map((s, i) => (i === indexAction ? s : ':id'));
    return {
        module,
        ressource,
        action,
        detail,
        chemin: `api/${[module, ressource, ...suite].join('/')}/`,
    };
}

export function estBruit(route: ApiRoute): boolean {
    return RESSOURCES_BRUIT.has(`${route.module}/*`)
        || RESSOURCES_BRUIT.has(`${route.module}/${route.ressource}`)
        || /-game$/.test(route.ressource)
        || (route.action !== null && ACTIONS_BRUIT.test(route.action));
}

export interface MatomoApiEvent {
    category: string;
    action: string;
    name: string;
}

/**
 * Événement d'une écriture réussie : Catégorie = application, Action = verbe
 * métier, Nom = objet (cf. `matomo-plan.ts`). Les lectures ne sont pas
 * mesurées : la page vue représente déjà la consultation.
 *
 * @param applicationEcran Application de l'écran courant, pour les modules d'API transverses.
 */
export function apiWriteEvent(method: string, url: string, applicationEcran: string): MatomoApiEvent | null {
    const methode = method.toUpperCase();
    const route = VERBES_CRUD[methode] ? parseApiUrl(url) : null;
    if (!route || estBruit(route)) {
        return null;
    }
    return {
        category: applicationDeLAppel(route, applicationEcran),
        action: verbeMetier(methode, route),
        name: libelleRessource(route),
    };
}

/** Événement d'un échec API (hors 401, géré par le rafraîchissement du jeton). */
export function apiErrorEvent(method: string, url: string, status: number, applicationEcran: string): MatomoApiEvent | null {
    if (status === 401) {
        return null;
    }
    const route = parseApiUrl(url);
    if (!route || estBruit(route)) {
        return null;
    }
    return {
        // Une seule catégorie pour tous les échecs ; l'application est dans le nom
        // (et dans la dimension Application) pour filtrer.
        category: 'Erreurs API',
        // 0 = réseau coupé, requête bloquée ou annulée par le navigateur.
        action: status === 0 ? 'Réseau' : String(status),
        name: `${applicationDeLAppel(route, applicationEcran)} · ${method.toUpperCase()} ${route.chemin}`,
    };
}
